// index.jsx
// Maintain state of plots, images at React App.js level
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import axios from "axios";
import handleAxiosError from "components/handleAxiosError";

import { CloudDownloadOutlined, Loop as LoopIcon } from "@mui/icons-material";

import { DownloadOutlined } from "@mui/icons-material";
import { Box, Button, useTheme, useMediaQuery, FormControlLabel, Checkbox, TextField } from "@mui/material";
// Import provider
import { usePlotData } from 'providers/PlotDataProvider.js'; // Corrected path if necessary
import "./RNA.css";
import { json } from 'react-router-dom';

let PYTHON_HOST_URL = "https://p1.bioengineai.com";  // python port 5001
//let PYTHON_HOST_URL = "https://p1.bioengineai.com";  // python port 5001
//let PYTHON_HOST_URL = "http://localhost:8000";  // backend python at port 5001

const CytoRNA = () => {
  const theme = useTheme();
  const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
  const [url, setUrl] = useState("");

  // download timer
  const [downloading, setDownloading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);

  // receivedMessages state is still local if not needed in context
  const [receivedMessages, setReceivedMessages] = useState([]);
  // streamingImages state is still local if not needed in context
  const [streamingImages, setStreamingImages] = useState(false);

  const [downloadViolin, setDownloadViolin] = useState(false);
  const [downloadUmap, setDownloadUmap] = useState(false);
  const [downloadDotplot, setDownloadDotplot] = useState(false);
  const [loadingSelectedPlots, setLoadingSelectedPlots] = useState(false);

  const isAnyPlotSelected = downloadViolin || downloadDotplot || downloadUmap;

  const {
    violinData, umapData, dotplotData, tableData,
    setViolinData, setUmapData, setDotplotData, setTableData,
    // Get image states and setters from context
    receivedImages, setReceivedImages,
    loadingImages, setLoadingImages
  } = usePlotData();

  const handleLoadFromS3 = async () => {
    if (!url.trim()) {
      alert(`Please enter a valid S3 URL.` ); // Slightly more informative alert
      return;
    }
    console.log("Passing s3_url to backend:", url);
    // No need to set local loadingSelectedPlots(true) here unless S3 load is tied to that specifically
    // If S3 load should also show a general loading state for images, consider using setLoadingImages(true)

    try {
      const response = await fetch(`${PYTHON_HOST_URL}/api/process_rna_from_s3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ s3_url: url }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jsonData = await response.json();
      console.log("Data loaded from S3:", jsonData);

      // Since receivedImages is now from context, these will update the global state
      if (jsonData.violin_plot) {
        setViolinData(jsonData.violin_plot); // Assuming violinData from context is for this specific plot
        // setReceivedImages(prevImages => [...prevImages, jsonData.violin_plot]); // Decide if S3 plots also go into the main stream
      }
      if (jsonData.umap_plot) {
        setUmapData(jsonData.umap_plot);
        // setReceivedImages(prevImages => [...prevImages, jsonData.umap_plot]);
      }
      if (jsonData.dot_plot) {
        setDotplotData(jsonData.dot_plot);
        // setReceivedImages(prevImages => [...prevImages, jsonData.dot_plot]);
      }
      // setLoadingSelectedPlots(false); // Manage based on actual S3 loading flow
    } catch (error) {
      console.error("Error loading data from S3:", error);
      // setLoadingSelectedPlots(false);
      handleAxiosError(error); // Ensure this function exists and is correctly imported/defined
    }
  };

  const handleFetchImagesProgressive = async () => {
    setLoadingImages(true); // Updates context state
    setReceivedImages([]);  // Updates context state, clearing previous images
    try {
      const response = await fetch(`${PYTHON_HOST_URL}/api/static_images`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const reader = response.body?.getReader();
      const decoder = new TextDecoder('utf-8');
      let accumulatedData = '';

      if (!reader) {
        console.error("Could not get reader from response body.");
        setLoadingImages(false); // Updates context state
        return;
      }

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          console.log("Image stream complete!");
          setLoadingImages(false); // Updates context state
          break;
        }
        if (value) {
          accumulatedData += decoder.decode(value);
          const lines = accumulatedData.split('\n');
          accumulatedData = lines.pop(); 

          for (const line of lines) {
            if (line.trim()) {
              try {
                const imageData = JSON.parse(line);
                if (imageData && imageData.image) {
                  setReceivedImages(prevImages => [...prevImages, imageData.image]); // Updates context state
                }
              } catch (error) {
                console.error("Error parsing JSON:", error, line);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching image stream:", error);
      setLoadingImages(false); // Updates context state
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box m="1.5rem 2.5rem">
      <FlexBetween>
        <Header title="CytoRNA" subtitle="AI for RNA Multi-Omics Biology" />
      </FlexBetween>

      <div className="input-area" style={{ marginTop: "auto" }}>
        <button onClick={handleLoadFromS3} className="send-btn">
          Load from S3
          {downloading && (
            <div style={{ marginTop: '10px' }}>
              ..       File(s) Downloading... {formatTime(elapsedTime)}
            </div>
          )}
        </button>

        <input
          type="text"
          placeholder="s3://cytohub-public-content/data/pbmc3k.h5ad"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="input-message"
          onKeyPress={(e) => e.key === "Enter" && handleLoadFromS3()}
        />
      </div>
      {downloading && <div style={{ marginTop: '10px' }}>downloading {url} ...</div>}

      <br></br>
      <p>Sample datasets:
          <li>s3://cytohub-public-content/data/pbmc3k.h5ad</li>
          <li>s3://cytohub-public-content/data/GSM6752838_EMM2.h5ad</li>
      </p>

      <div>
        <h1>scRNA-seq Analytics</h1>
        <div className="input-area" style={{ marginTop: "auto" }}>
        <input
          type="text"
          placeholder="Violin Plot, UMAP, HeatMap, DotPlot"
          // value={url} // This input seems to re-use 'url' state, intentional?
          // onChange={(e) => setUrl(e.target.value)}
          className="input-message"
          // onKeyPress={(e) => e.key === "Enter" && handleFetchImagesProgressive()}
        />
        <button onClick={handleFetchImagesProgressive} className="send-btn" style={{ marginRight: '10px' }}>
          Load Images Progressively
        </button>
        </div>
        {/* This loading message is general, consider if it should be tied to context's loadingImages */}
        {loadingImages && <div style={{ marginTop: '10px' }}>Loading ...</div>} 

      <br></br>

      <div>
        <h3>scRNA-seq plots and images</h3>
        <Box // OUTER Box for plots area
          mt="20px" display="grid" gap="20px"
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gridAutoRows="minmax(160px, auto)"
          sx={{
            "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
          }}
        >
            {/* Grid for the progressively loaded images */}
            {/* Render if images exist OR if actively loading (to show spinner) */}
            {(receivedImages.length > 0 || loadingImages) && (
            <Box // INNER Box for the image grid itself
                mt="20px" 
                display="grid" 
                gap="10px"
                // MODIFIED gridTemplateColumns for max 5 items, responsive:
                gridTemplateColumns="repeat(auto-fit, minmax(max(150px, calc((100% - 40px) / 5)), 1fr))"
                sx={{
                "& > div": { // Styles for each image/spinner cell
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                },
                "& > div > img": {
                    maxWidth: '100%',
                    height: 'auto',
                },
                }}
            >
                {receivedImages.map((base64Image, index) => (
                <Box key={index}>
                    <img
                    src={`data:image/png;base64,${base64Image}`}
                    alt={`Image ${index + 1}`}
                    />
                </Box>
                ))}
                {/* Spinner for the next image slot */}
                 {loadingImages && (
                  <Box key="loading-spinner-slot-1"> 
                    <LoopIcon className="icon-spin" style={{ fontSize: '2.5rem', color: theme.palette.text.secondary }} />
                 </Box>
                )}
            </Box>
            )}
            
            {/* Standalone loading messages */}
            {loadingImages && receivedImages.length < 2 && <p>Loading images...</p>}
            {receivedImages.length === 0 && !loadingImages && <p>No images loaded or an error occurred.</p>}
            {receivedImages.length > 0 && receivedImages.length < 10 && !loadingImages && <p>Loading remaining images...</p>}
            {receivedImages.length === 10 && <p>All 10 images loaded.</p>}

          <Box gridColumn="span 6"> {/* Matplotlib Plot */}
            <h2>Violin Plot</h2>
            {umapData && ( // Assuming umapData from context is intended for the violin plot display here
              <img
                src={`data:image/png;base64,${umapData}`}
                alt="Violin Plot"
                style={{ maxWidth: "100%" }} 
              />
            )}
            {!umapData && !loadingSelectedPlots && <p>Loading violin plot...</p>}
            {loadingSelectedPlots && downloadViolin && !umapData && <p>Loading Violin Plot...</p>}
          </Box>

          <Box gridColumn="span 6"> {/* Matplotlib Plot */}
            <h2>BioMarker Dotplot</h2>
            {dotplotData && (
              <img
                src={`data:image/png;base64,${dotplotData}`}
                alt="Dot Plot"
                style={{ maxWidth: "100%" }}
              />
            )}
            {!dotplotData && !loadingSelectedPlots && <p>Loading dot plot...</p>}
            {loadingSelectedPlots && downloadDotplot && !dotplotData && <p>Loading Dot Plot...</p>}
          </Box>
        </Box>
      </div>
      </div>
    </Box>
  );
};
export default CytoRNA;
